import create from 'zustand';

// Import entities from shared source (same code used by API and UI)
import Robot from './entities/Robot';
import Almacen from './entities/Almacen';
import Ikasmak from './entities/Ikasmak';
import Medicion from './entities/Medicion';
import NotificationSystem from './entities/NotificationSystem';
import ErrorMessages from './entities/ErrorMessages';

// Create initial entity instances
const initialRobot = new Robot();
const initialAlmacen = new Almacen();
const initialIkasmak = new Ikasmak();
const initialMedicion = new Medicion();
const initialNotifications = new NotificationSystem();

const useStore = create((set, get) => ({
    // Entity instances (same as API uses)
    robot: initialRobot,
    almacen: initialAlmacen,
    ikasmak: initialIkasmak,
    medicion: initialMedicion,
    notifications: initialNotifications,

    // Computed state for UI (derived from entities)
    robotLocation: initialRobot.getLocation(),
    robotArmState: initialRobot.getArmState(),
    openDrawer: initialAlmacen.isOpen(),
    openMachineDoor: initialIkasmak.isOpen(),

    // Logging
    systemLog: "",
    errorLog: "",

    // Actions to update entities
    setRobotLocation: (location) => {
        const { robot } = get();
        robot.moveTo(location);
        set({ robotLocation: robot.getLocation(), robot });
    },

    setRobotArmState: (arm_state) => {
        const { robot } = get();
        robot.setArmState(arm_state);
        set({ robotArmState: robot.getArmState(), robot });
    },

    setOpenDrawer: (newState) => {
        const { almacen } = get();
        if (newState) {
            almacen.open();
        } else {
            almacen.close();
        }
        set({ openDrawer: almacen.isOpen(), almacen });
    },

    setOpenMachineDoor: (newState) => {
        const { ikasmak } = get();
        if (newState) {
            ikasmak.open();
        } else {
            ikasmak.close();
        }
        set({ openMachineDoor: ikasmak.isOpen(), ikasmak });
    },

    // Get entity states
    getRobotState: () => {
        const { robot } = get();
        return robot.getState();
    },

    getAlmacenState: () => {
        const { almacen } = get();
        return almacen.getState();
    },

    getIkasmakState: () => {
        const { ikasmak } = get();
        return ikasmak.getState();
    },

    getMedicionState: () => {
        const { medicion } = get();
        return medicion.getState();
    },

    getNotificationsState: () => {
        const { notifications } = get();
        return notifications.getState();
    },

    // Logging actions
    setSystemLog: (log) => set({ systemLog: log }),
    addToSystemLog: (log) => set((state) => ({ systemLog: state.systemLog + "<br/>" + log })),
    clearSystemLog: () => set({ systemLog: "" }),

    setErrorLog: (log) => set({ errorLog: log }),
    addToErrorLog: (errorData) => set((state) => {
        if (typeof errorData === 'string') {
            return { errorLog: state.errorLog + "<br/>" + errorData };
        } else if (errorData.logMessage && errorData.errorMessage) {
            return {
                systemLog: state.systemLog + "<br/>" + errorData.logMessage,
                errorLog: state.errorLog + "<br/>" + errorData.errorMessage
            };
        }
        return state;
    }),
    clearErrorLog: () => set({ errorLog: "" }),
    resetErrorSession: () => {
        ErrorMessages.resetSession();
    },

    // Sync UI state with entities (call after entity operations)
    // CRITICAL: Creates new object references so Zustand/React detects changes
    syncUIState: () => {
        const { robot, almacen, ikasmak, medicion, notifications } = get();

        // Create new instances with same prototype but new object reference
        // This forces Zustand to trigger re-renders
        const newRobot = Object.assign(Object.create(Object.getPrototypeOf(robot)), robot);
        const newAlmacen = Object.assign(Object.create(Object.getPrototypeOf(almacen)), almacen);
        const newIkasmak = Object.assign(Object.create(Object.getPrototypeOf(ikasmak)), ikasmak);
        const newMedicion = Object.assign(Object.create(Object.getPrototypeOf(medicion)), medicion);
        const newNotifications = Object.assign(Object.create(Object.getPrototypeOf(notifications)), notifications);

        set({
            robot: newRobot,
            almacen: newAlmacen,
            ikasmak: newIkasmak,
            medicion: newMedicion,
            notifications: newNotifications,
            robotLocation: robot.getLocation(),
            robotArmState: robot.getArmState(),
            openDrawer: almacen.isOpen(),
            openMachineDoor: ikasmak.isOpen()
        });
    }
}));

export default useStore;
